import * as React from "react";
import { useEffect, useState } from "react";

import { addMonths, startOfMonth } from "date-fns";
import {
  BadgeDollarSign,
  BriefcaseBusiness,
  Check,
  CircleQuestionMark,
  Clock3,
  Download,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

import { Mastercard } from "src/components/icon/Mastercard";
import { Visa } from "src/components/icon/Visa";
import { SettingsSection } from "src/components/SettingsDialog/SettingsSection";
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import { Checkbox } from "src/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { Label } from "src/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { Textarea } from "src/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { ADVISORY_ADDON_LABELS, ADVISORY_ADDONS } from "src/lib/advisory";
import { numberFormat, toCapitalized } from "src/lib/format";
import { useApiMutation, useApiQuery } from "src/lib/hooks/useApi";
import { VOLUME_BANDS } from "src/lib/pricing";
import {
  ACCEPT_STORE_ADVISORY_QUOTE_MUTATION,
  CANCEL_STORE_ADVISORY_MUTATION,
  CURRENT_USAGE_QUERY,
  INVOICE_DOWNLOAD_URL_QUERY,
  INVOICES_QUERY,
  PAYMENT_METHOD_QUERY,
  PAYMENT_METHOD_URL_QUERY,
  REQUEST_STORE_ADVISORY_MUTATION,
} from "src/lib/queries/billing";

const paymentMethodIcons = {
  mastercard: Mastercard,
  visa: Visa,
};
const localInvoiceExternalIds = new Set([
  "no-payment-method",
  "trial-fully-covered",
]);

const coreAdvisoryServices = [
  "Initial business audit",
  "White-glove financial setup and data review",
  "Custom reporting for KPIs and targets",
  "Monthly strategic review and recommendations",
  "Decision support for growth, profitability, and cash flow",
  "Unlimited AI usage, subject to fair use",
];

const formatVolumeBand = (band) =>
  band.max === null
    ? "Above 100,000 orders"
    : `${band.min.toLocaleString("en-US")} - ${band.max.toLocaleString("en-US")} orders`;

const formatVolumeBandKey = (volumeBandKey) => {
  const band = VOLUME_BANDS.find(({ key }) => key === volumeBandKey);
  return band ? formatVolumeBand(band) : "Current volume band";
};

const VolumePricingTooltip = () => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground focus-visible:ring-ring inline-flex size-5 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:outline-none"
        aria-label="View volume pricing"
      >
        <CircleQuestionMark className="size-3.5" />
      </button>
    </TooltipTrigger>
    <TooltipContent side="top" sideOffset={6} className="w-72 max-w-none p-3">
      <div className="font-medium">Monthly platform pricing</div>
      <div className="text-muted-foreground mt-1">
        Trailing 365-day order count ÷ 12. Prices are per store/month.
      </div>
      <div className="mt-3 space-y-1.5">
        {VOLUME_BANDS.map((band) => (
          <div
            key={band.key}
            className="flex items-baseline justify-between gap-4"
          >
            <span>{formatVolumeBand(band).replace(" orders", "")}</span>
            <span className="shrink-0 font-medium">
              {band.customQuote
                ? "Custom*"
                : `$${(band.priceCents / 100).toLocaleString("en-US")}/mo`}
            </span>
          </div>
        ))}
      </div>
      <div className="text-muted-foreground mt-3 border-t pt-2">
        *Above 100,000 orders is provisionally billed at $1,299/month while we
        prepare a custom quote.
      </div>
    </TooltipContent>
  </Tooltip>
);

const AdvisoryServicesList = ({ addons = [], customServices = [] }) => {
  const additionalServices = [
    ...addons.map((addon) => ADVISORY_ADDON_LABELS[addon] || addon),
    ...customServices,
  ];

  return (
    <>
      <div className="font-medium">Included in Advisory</div>
      <div className="mt-2 space-y-1.5">
        {coreAdvisoryServices.map((service) => (
          <div key={service} className="flex items-start gap-2">
            <Check className="text-primary mt-0.5 size-3.5 shrink-0" />
            <span>{service}</span>
          </div>
        ))}
      </div>

      {additionalServices.length ? (
        <div className="mt-3 border-t pt-3">
          <div className="font-medium">Additional services</div>
          <div className="mt-2 space-y-1.5">
            {additionalServices.map((service, index) => (
              <div
                key={`${index}-${service}`}
                className="flex items-start gap-2"
              >
                <Check className="text-primary mt-0.5 size-3.5 shrink-0" />
                <span>{service}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
};

const AdvisoryServicesTooltip = ({ addons = [], customServices = [] }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground focus-visible:ring-ring inline-flex size-5 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:outline-none"
        aria-label="View included Advisory services"
      >
        <CircleQuestionMark className="size-3.5" />
      </button>
    </TooltipTrigger>
    <TooltipContent side="top" sideOffset={6} className="w-80 max-w-none p-3">
      <AdvisoryServicesList addons={addons} customServices={customServices} />
    </TooltipContent>
  </Tooltip>
);

const AdvisoryControl = ({ usage, refreshUsage }) => {
  const [dialog, setDialog] = useState(null);
  const [message, setMessage] = useState("");
  const [addons, setAddons] = useState([]);

  const onMutationSuccess = async (title) => {
    toast.success(title);
    setDialog(null);
    await refreshUsage();
  };

  const { mutationApi: requestAdvisory, loading: requesting } = useApiMutation({
    mutation: REQUEST_STORE_ADVISORY_MUTATION,
    onSuccess: () => onMutationSuccess("Advisory request sent"),
  });
  const { mutationApi: acceptQuote, loading: accepting } = useApiMutation({
    mutation: ACCEPT_STORE_ADVISORY_QUOTE_MUTATION,
    onSuccess: () => onMutationSuccess("Advisory is now active"),
  });
  const { mutationApi: cancelAdvisory, loading: cancelling } = useApiMutation({
    mutation: CANCEL_STORE_ADVISORY_MUTATION,
    onSuccess: () => onMutationSuccess("Advisory will end after this month"),
  });

  const toggleAddon = (addon, checked) => {
    setAddons((current) =>
      checked
        ? [...new Set([...current, addon])]
        : current.filter((item) => item !== addon)
    );
  };

  const status = usage.advisoryStatus;
  const quotedPrice = Number.isInteger(usage.advisoryMonthlyPriceCents)
    ? numberFormat.currency(usage.advisoryMonthlyPriceCents / 100)
    : null;

  return (
    <>
      {!status || status === "ENDED" ? (
        <Button
          size="xs"
          variant="secondary"
          onClick={() => setDialog("request")}
        >
          Request Advisory
        </Button>
      ) : status === "REQUESTED" ? (
        <Badge
          variant="outline"
          className="gap-1.5 border-primary/35 bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-foreground dark:border-primary/50 dark:bg-primary/15"
        >
          <Clock3 className="size-3.5 text-primary" />
          Request received
        </Badge>
      ) : status === "QUOTED" ? (
        <Button size="xs" onClick={() => setDialog("accept")}>
          Review {quotedPrice} quote
        </Button>
      ) : status === "CANCEL_AT_MONTH_END" ? (
        <div className="text-right text-xs">
          <Badge variant="secondary">Ends this month</Badge>
          {usage.advisoryCancelAt ? (
            <div className="text-muted-foreground mt-1">
              Advisory add-on ends{" "}
              {new Date(usage.advisoryCancelAt).toLocaleDateString()}
            </div>
          ) : null}
        </div>
      ) : (
        <Button size="xs" variant="ghost" onClick={() => setDialog("cancel")}>
          Manage Advisory
        </Button>
      )}

      <Dialog
        open={dialog === "request"}
        onOpenChange={(open) => !open && setDialog(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Lucris Advisory</DialogTitle>
            <DialogDescription>
              Starting at $1,500/month. We will review your goals and send a
              store-specific monthly quote before anything is charged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-3">
              <div className="text-sm font-medium">Optional services</div>
              {ADVISORY_ADDONS.map(([value, label]) => (
                <Label key={value} className="items-start font-normal">
                  <Checkbox
                    checked={addons.includes(value)}
                    onCheckedChange={(checked) =>
                      toggleAddon(value, checked === true)
                    }
                    className="rounded-xs"
                  />
                  <span className="leading-4">{label}</span>
                </Label>
              ))}
            </div>
            <div className="space-y-2">
              <Label htmlFor={`advisory-message-${usage.integrationId}`}>
                What should we know?
              </Label>
              <Textarea
                id={`advisory-message-${usage.integrationId}`}
                value={message}
                maxLength={4000}
                placeholder="Tell us about your goals, reporting needs, or custom systems."
                onChange={(event) => setMessage(event.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)}>
              Cancel
            </Button>
            <Button
              disabled={requesting}
              onClick={() =>
                requestAdvisory({
                  input: {
                    integrationId: usage.integrationId,
                    requestedAddons: addons,
                    message: message.trim() || null,
                  },
                })
              }
            >
              {requesting ? <Loader2 className="animate-spin" /> : null}
              Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={dialog === "accept"}
        onOpenChange={(open) => !open && setDialog(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Activate Lucris Advisory</DialogTitle>
            <DialogDescription>
              Your quote is {quotedPrice}/month. Accepting starts Advisory and
              unlimited fair-use AI immediately. The full current month will be
              added to your next monthly invoice.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-muted/30 max-h-72 overflow-y-auto rounded-lg p-4 text-sm">
            <AdvisoryServicesList
              addons={usage.advisoryQuotedAddons}
              customServices={usage.advisoryQuotedCustomServices}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)}>
              Not yet
            </Button>
            <Button
              disabled={accepting}
              onClick={() =>
                acceptQuote({ integrationId: usage.integrationId })
              }
            >
              {accepting ? <Loader2 className="animate-spin" /> : null}
              Accept and activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={dialog === "cancel"}
        onOpenChange={(open) => !open && setDialog(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Lucris Advisory</DialogTitle>
            <DialogDescription>
              Advisory and unlimited fair-use AI are active for this store.
              Cancellation takes effect at the end of the current month.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)}>
              Keep Advisory
            </Button>
            <Button
              variant="destructive"
              disabled={cancelling}
              onClick={() =>
                cancelAdvisory({ integrationId: usage.integrationId })
              }
            >
              {cancelling ? <Loader2 className="animate-spin" /> : null}
              Cancel at month end
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const UsageTable = () => {
  const { data, queryApi } = useApiQuery({
    query: CURRENT_USAGE_QUERY,
  });
  const { currentUsage } = data || {};

  useEffect(() => {
    queryApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total =
    (currentUsage || []).reduce(
      (total, usage) => total + usage.amountCents,
      0
    ) / 100;
  const nextBillingDate = startOfMonth(
    addMonths(new Date(), 1)
  ).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const platformUsage = (currentUsage || []).filter(
    (usage) => usage.kind === "PLATFORM"
  );
  const advisoryUsage = (currentUsage || []).filter(
    (usage) => usage.kind === "ADVISORY"
  );

  return (
    <div className="overflow-x-auto rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted hover:bg-muted">
            <TableHead>Store</TableHead>
            <TableHead>Pricing basis</TableHead>
            <TableHead className="text-right">Projected charge</TableHead>
            <TableHead className="text-right">Advisory</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody className="w-full">
          {platformUsage.map((usage) => (
            <TableRow key={`${usage.integrationId}-platform`}>
              <TableCell className="font-medium">{usage.storeName}</TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <span>
                    {usage.pricingSource === "ADMIN_OVERRIDE"
                      ? "Custom platform price"
                      : formatVolumeBandKey(usage.volumeBandKey)}
                  </span>
                  {usage.pricingSource === "ADMIN_OVERRIDE" ? null : (
                    <VolumePricingTooltip />
                  )}
                </div>
                <div className="text-muted-foreground text-xs">
                  {usage.averageMonthlyOrders == null
                    ? "Pricing snapshot pending"
                    : `${Math.round(usage.averageMonthlyOrders).toLocaleString()} average monthly orders`}
                </div>
              </TableCell>
              <TableCell className="text-right">
                {numberFormat.currency(usage.amountCents / 100)}
                <div className="text-muted-foreground text-xs">
                  {usage.activeDaysCount} connected day
                  {usage.activeDaysCount === 1 ? "" : "s"}
                </div>
              </TableCell>
              <TableCell className="text-right">
                {["ACTIVE", "CANCEL_AT_MONTH_END"].includes(
                  usage.advisoryStatus
                ) ? null : (
                  <AdvisoryControl usage={usage} refreshUsage={queryApi} />
                )}
              </TableCell>
            </TableRow>
          ))}

          {advisoryUsage.map((usage) => (
            <TableRow
              key={`${usage.integrationId}-advisory`}
              className="bg-muted/25"
            >
              <TableCell>
                <div className="flex items-center gap-2 font-medium">
                  <BriefcaseBusiness className="text-primary size-4" /> Advisory
                  Add-on
                </div>
              </TableCell>
              <TableCell>
                <div className="text-muted-foreground flex items-center gap-1 text-xs">
                  <span>{usage.storeName}</span>
                  <AdvisoryServicesTooltip
                    addons={usage.advisoryQuotedAddons}
                    customServices={usage.advisoryQuotedCustomServices}
                  />
                </div>
              </TableCell>
              <TableCell className="text-right">
                {numberFormat.currency(usage.amountCents / 100)}
              </TableCell>
              <TableCell className="text-right">
                <AdvisoryControl usage={usage} refreshUsage={queryApi} />
              </TableCell>
            </TableRow>
          ))}

          <TableRow>
            <TableCell colSpan={4}>
              <div className="flex flex-col items-end">
                <div className="inline-flex gap-1">
                  <span className="text-muted-foreground">Total:</span>
                  <span>{numberFormat.currency(total)} USD</span>
                </div>
                <span className="text-muted-foreground text-xs">
                  Next payment due on {nextBillingDate}
                </span>
              </div>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

const PaymentMethod = () => {
  const { data, queryApi, loading } = useApiQuery({
    query: PAYMENT_METHOD_QUERY,
  });
  const { queryApi: queryPaymentMethodUrl, loading: loadingPaymentMethodUrl } =
    useApiQuery({
      query: PAYMENT_METHOD_URL_QUERY,
    });

  const { paymentMethod } = data || {};
  const hasPaymentMethod = !!paymentMethod?.last4;

  useEffect(() => {
    queryApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPaymentMethodPage = async () => {
    const { data } = await queryPaymentMethodUrl();

    if (data?.paymentMethodUrl) {
      window.open(data.paymentMethodUrl, "_blank");
    } else {
      toast.error("Failed to process your request. Please try again.");
    }
  };

  return (
    <SettingsSection
      title="Payment Method"
      description="Charges are applied to your payment method on the first day of the month"
    >
      {hasPaymentMethod ? (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-muted flex h-10 w-14 items-center justify-center rounded">
              {paymentMethodIcons[paymentMethod.type.toLowerCase()] ? (
                React.createElement(
                  paymentMethodIcons[paymentMethod.type.toLowerCase()]
                )
              ) : (
                <BadgeDollarSign />
              )}
            </div>

            <div className="flex flex-col">
              <div className="text-sm">
                **** **** **** {paymentMethod.last4}
              </div>
              <div className="text-muted-foreground text-xs">
                Expires {paymentMethod.expMonth}/{paymentMethod.expYear}
              </div>
            </div>
          </div>

          <Button
            variant="secondary"
            disabled={loading || loadingPaymentMethodUrl}
            onClick={openPaymentMethodPage}
          >
            Update
          </Button>
        </div>
      ) : (
        <Button
          className="ml-auto w-fit"
          disabled={loading || loadingPaymentMethodUrl}
          onClick={openPaymentMethodPage}
        >
          Add Payment Method
        </Button>
      )}
    </SettingsSection>
  );
};

const Invoices = () => {
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const { data, queryApi } = useApiQuery({
    query: INVOICES_QUERY,
  });
  const { queryApi: queryInvoiceDownloadUrl } = useApiQuery({
    query: INVOICE_DOWNLOAD_URL_QUERY,
    onError: () => "Unable to refresh the invoice link. Please try again.",
  });

  const { invoices = [] } = data || {};

  useEffect(() => {
    queryApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openInvoice = async (invoice) => {
    setDownloadingInvoiceId(invoice.id);

    try {
      const { data } = await queryInvoiceDownloadUrl({ invoiceId: invoice.id });
      const url = data?.invoiceDownloadUrl;

      if (!url) {
        toast.error("Invoice link unavailable", {
          description: "This invoice does not have a downloadable link.",
        });
        return;
      }

      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  return (
    <SettingsSection
      title="Invoices"
      description="View and download your past invoices"
    >
      <div className="overflow-hidden rounded-lg">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted hover:bg-muted">
              <TableHead>Invoice number</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Charge</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((invoice) => (
              <TableRow key={invoice.id}>
                <TableCell>{invoice.number || invoice.id}</TableCell>
                <TableCell>{invoice.month}</TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <span
                      className={`${
                        invoice.status === "PAID"
                          ? "text-green-600"
                          : "text-destructive"
                      }`}
                    >
                      {toCapitalized(invoice.status)}
                    </span>

                    {invoice.status !== "PAID" && (
                      <Tooltip>
                        <TooltipTrigger>
                          <CircleQuestionMark className="text-muted-foreground ml-2 size-4" />
                        </TooltipTrigger>
                        <TooltipContent>
                          Click the <Download className="inline size-3" />{" "}
                          button to pay the invoice manually.
                          <br />
                          <br />
                          If you&apos;ve already paid, it may take a few hours
                          to update the status.
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {numberFormat.currency(invoice.amount / 100)}
                </TableCell>
                <TableCell className="text-right">
                  {invoice.externalId &&
                  !localInvoiceExternalIds.has(invoice.externalId) ? (
                    <Button
                      size="icon"
                      variant="ghost"
                      disabled={downloadingInvoiceId === invoice.id}
                      onClick={() => openInvoice(invoice)}
                    >
                      {downloadingInvoiceId === invoice.id ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Download />
                      )}
                    </Button>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </SettingsSection>
  );
};

const BillingSettings = () => {
  return (
    <div className="h-full space-y-6">
      <UsageTable />
      <PaymentMethod />
      <Invoices />
    </div>
  );
};

export default BillingSettings;
